import { isDefined } from '@sama/js-core';
import { flatten, times } from 'lodash';
import { BufferGeometry, Line, Vector3 } from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { Point3D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { H3BasicMaterial } from '../../../materials/basic/h3-basic-material';
import { PointCloudContentState } from '../../../state/point-cloud-content-state';
import { pointToVector } from '../../../utils/point-cloud-utils';
import { determineRendererLayers } from '../../../utils/shape-visibility-utils';
import { MaterialService } from '../../material.service';
import { setRendererLayers } from '../../render-utils';
import {
  AbstractMeshHelper,
  ConfigureMeshesConfiguration,
  RenderConfiguration,
} from '../abstract-mesh-helper';
import { getShapeColorIfHighlighted } from '../color-utils';
import {
  getOutlinedSphereMaterialParameters,
  OutlinedSphere,
} from '../outlined-sphere';
import { H3LineMaterial } from './h3-line-material';
import {
  getSubPointLayers,
  getThickLineLayers,
  getThinLineLayers,
} from './polyshape-utils';

/** @deprecated used for old projects without objects */
export const defaultPolyShapeColor = '#56b8af';
export const subpointRadius = 0.1;
const inBetweenExtraPoints = 10;

export abstract class AbstractPolyShapeHelper extends AbstractMeshHelper {
  thickLine: Line2;
  protected thinLine: Line<BufferGeometry, H3BasicMaterial>;
  protected points: OutlinedSphere[] = [];
  protected polylinePositions: Point3D[] = [];
  protected abstract lineWidth: number;

  constructor(
    public index: number,
    protected shapeStoreKey: 'polygons' | 'lines',
  ) {
    super();

    this.thickLine = new Line2(new LineGeometry());
    this.thickLine.name = this.getPolyShapeName();
    this.thickLine.userData = {
      shapeIndex: this.index,
      meshType: this.getPolyShapeType(),
    };

    this.thinLine = new Line();
    this.thinLine.name = this.getPolyShapeName();
    this.thinLine.userData = {
      shapeIndex: this.index,
      meshType: this.getPolyShapeType() + '-projection',
    };
    this.thinLine.frustumCulled = false;
  }

  override getMeshes() {
    return [this.thickLine, this.thinLine];
  }

  getSubPoints() {
    return this.points;
  }

  update(points: Point3D[], addInBetweenPoints: boolean) {
    this.thickLine.geometry.setPositions(flatten(points));
    this.addPoints(points);

    if (addInBetweenPoints) {
      this.polylinePositions = this.calculateInBetweenPoints(points);
    } else {
      this.polylinePositions = points;
    }
    this.thinLine.geometry.setFromPoints(
      this.polylinePositions.map(pointToVector),
    );
    this.thinLine.computeLineDistances();
  }

  dispose() {
    this.thickLine.geometry.dispose();
    this.disposePoints();
  }

  protected override updateRendererLayers(
    state: PointCloudContentState,
    renderConfig: RenderConfiguration,
  ) {
    const shape = this.getShapeState(state);
    const layers = determineRendererLayers(shape, state, renderConfig);

    const thickLineLayers = getThickLineLayers(state, renderConfig, layers);
    const thinLineLayers = getThinLineLayers(state, renderConfig, layers);
    const pointLayers = getSubPointLayers(state, renderConfig, layers);

    setRendererLayers(thickLineLayers, this.thickLine);
    setRendererLayers(thinLineLayers, this.thinLine);
    setRendererLayers(pointLayers, this.points);
  }

  protected override configureMeshes(config: ConfigureMeshesConfiguration) {
    const { state, materialService } = config;
    const shapeColor = this.getShapeColor(state);

    this.thickLine.material = materialService.get(H3LineMaterial, {
      name: this.shapeStoreKey,
      color: shapeColor,
      resolution: state.render.containerSize[config.outputName],
      linewidth: this.lineWidth,
      vertexColors: false,
      alphaToCoverage: true,
      opacity: 1,
    });

    this.thinLine.material = materialService.get(H3BasicMaterial, {
      name: this.shapeStoreKey,
      color: shapeColor,
      opacity: 1,
    });

    this.configurePoints(shapeColor, materialService);
  }

  protected getShapeColor(state: PointCloudContentState) {
    return getShapeColorIfHighlighted(this.getShapeState(state), state);
  }

  protected getShapeState(state: PointCloudContentState) {
    return state.render[this.shapeStoreKey][this.index];
  }

  protected addPoints(points: Point3D[]) {
    this.disposePoints();
    points.forEach((point, index) => {
      const pointMesh = new OutlinedSphere(subpointRadius, {
        color: '#' + this.thickLine.material.color.getHexString(),
        opacity: 0.6,
        transparent: true,
      });

      pointMesh.setUserData({ ...this.thickLine.userData, index });
      pointMesh.position.set(...point);

      this.points.push(pointMesh);
      this.thickLine.add(pointMesh);
    });
  }

  private configurePoints(color: string, materialService: MaterialService) {
    const materialsParameters = getOutlinedSphereMaterialParameters({
      color,
      opacity: 0.6,
    });
    const sphereMaterial = materialService.get(
      H3BasicMaterial,
      materialsParameters.sphere,
    );

    this.points.forEach((point) => {
      point.setMaterial(sphereMaterial);
    });
  }

  private calculateInBetweenPoints(points: Point3D[]) {
    const newPoints: Point3D[] = [];

    const newPoint = new Vector3();
    const currentPoint = new Vector3();
    const nextPoint = new Vector3();

    for (const [index, point] of points.entries()) {
      newPoints.push(point);

      if (!isDefined(points[index + 1])) {
        continue;
      }
      nextPoint.fromArray(points[index + 1]);
      currentPoint.fromArray(point);

      const totalPointsInSegment = inBetweenExtraPoints + 2;
      const scaleFactor = 1 / (totalPointsInSegment - 1);

      times(inBetweenExtraPoints, (i) => {
        const pointScale = scaleFactor * (i + 1);
        newPoints.push(
          newPoint.lerpVectors(currentPoint, nextPoint, pointScale).toArray(),
        );
      });
    }

    return newPoints;
  }

  private disposePoints() {
    this.points.forEach((point) => {
      this.thickLine.remove(point);
      point.dispose();
    });
    this.points = [];
  }

  abstract getPolyShapeType(): string;
  abstract getPolyShapeName(): string;
}
